/**
 * GFG-194 — the campaign-contacts modal from the approved prototype
 * (local/tasks/GFG-194/attachments/gfg-admin-dashboard.html: `cmp-contacts-modal`, line 4265;
 * behaviour in `openContactsModal` / `renderContactsTable` / `cmpFilteredContacts`, line 10913).
 *
 * REUSABLE BY CONSTRUCTION: it takes its data as props and fetches nothing — no hook, no api
 * module, nothing send-queue-shaped is imported here. Its predecessor
 * (`components/admin/sendQueueCampaigns/ViewContactsDialog.tsx`) called `useSendQueueContacts` itself,
 * which welded it to one endpoint and made it unusable on any other screen. Every caller owns its
 * own request and hands over `contacts` / `total` / `loading` / `error`, so the same modal serves
 * the Send Queue, the Campaigns table and anything else that can produce `CampaignContactRow[]`.
 *
 * MUI's `Dialog` supplies the behaviour (portal, focus trap, focus restore on close, and — via the
 * un-overridden default `onClose` wiring — Escape and backdrop dismissal); appearance is Tailwind
 * on the repo's design tokens. `GfgModal` was considered and rejected: it is a confirm dialog with
 * a fixed title/body/two-button shape, not a 1280px-wide data surface.
 *
 * THE PER-ROW ACTIONS REACH AN API THE SAME WAY: they don't. The prototype's three icon controls
 * (resend the gift e-mail, preview the personalised one, remove the contact) and its inline e-mail
 * editing are CALLBACK PROPS — `onResend`, `onPreviewEmail`, `onRemove`, `onEmailChange` — and the
 * modal still imports no hook and calls no endpoint. What it does own is the REFUSALS: the
 * campaign lock, the resendable-status rule, the 24-hour cooldown and the address check all run
 * here, before any callback fires, so every caller inherits the prototype's rules instead of
 * reimplementing them.
 *
 * AN ACTION NOBODY CAN PERFORM IS NOT RENDERED. Supply no callback and there is no actions column
 * at all; supply two and exactly those two icons appear. That is the rule that replaces a disabled
 * placeholder — a control that can never do anything is worse than no control.
 *
 * `actionsDisabled` IS THE ONE EXCEPTION, AND IT IS DELIBERATE. Review asked for the MVP to show
 * the prototype's three icons greyed out rather than absent (2026-08-27), so the column that ships
 * next is visibly reserved instead of appearing later out of nowhere. Set the flag and every icon
 * the caller supplied renders truly `disabled` — not the `aria-disabled` treatment in
 * ContactActionButton.tsx, which exists to DELIVER a refusal sentence on click. There is no
 * sentence to deliver here: the action is not refused, it is not built yet, and inventing copy that
 * says so would be a message to rewrite the day it ships. So the control is inert and carries
 * only its own name.
 *
 * One shared table for the Send Queue and My Campaigns (GFG-194/GFG-364); the name editor lives in
 * ContactNameEditor.tsx and the row-action cell in ContactRowActions.tsx. A pending or bounced
 * name or address is editable inline, and read-only when the caller does not allow it; hover hints
 * come only from the optional `fieldHint` prop.
 */
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, Button, Dialog, IconButton, Skeleton, Snackbar, Tooltip } from "@mui/material";
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import {
  ALL_STATUSES,
  COPY,
  campaignContactsCsvFilename,
  INLINE_TEXT_INPUT_CLASS,
} from "@/constants/campaignContacts";
import type { CampaignLinkStatus } from "@/constants/campaings";
import { downloadCsv } from "@/lib/csv";
import { resolveScheduleTimeZone } from "@/utils/timezone-helper";
import ContactNameEditor, { ContactNameCells } from "./ContactNameEditor";
import { CampaignContactActionsCell, NewContactTag } from "./ContactRowActions";
import ContactSendDateEditor from "./ContactSendDateEditor";
import {
  CONTACTS_TABLE_PAPER_SX,
  CONTACTS_TD_CLASS,
  CONTACTS_TEXT_LINK_SX,
  CONTACTS_TH_CLASS,
  ContactsDialogHeader,
} from "./ContactsDialog";
import {
  buildCampaignContactsCsv,
  campaignContactsColumns,
  contactStatusesPresent,
  contactTableColumnCount,
  contactUrl,
  displayStatus,
  filterCampaignContacts,
  formatContactDate,
  formatSendInstant,
  isRemovable,
  isResendable,
  isValidContactEmail,
  REDEEMED_STATUS,
  resendCooldownHoursLeft,
} from "./campaignContacts-helper";
import type { CampaignContactRow, CampaignContactsLock } from "./types";

export interface CampaignContactsModalProps {
  open: boolean;
  /** Fired by the close control, the footer's Close, Escape and a backdrop click alike. */
  onClose: () => void;
  /** Shown in the subtitle and used as the CSV filename stem. */
  campaignName: string;
  /** The rows to show. Filtering and search happen here, over this list; nothing is re-fetched. */
  contacts: CampaignContactRow[];
  /**
   * The campaign's true recipient count. Defaults to `contacts.length`; pass it when the endpoint
   * hands over fewer rows than it counted, and a line under the table says how many of how many
   * are shown. The Send Queue's endpoint is uncapped, so the two agree there and the line is
   * silent.
   */
  total?: number;
  /** The caller's request is in flight — skeleton rows instead of the table. */
  loading?: boolean;
  /** A ready-to-read failure message, or null. The caller humanises its own error. */
  error?: string | null;
  /** Why these contacts can't be managed, or null. See `CampaignContactsLock`. */
  lock?: CampaignContactsLock | null;
  /**
   * Resend this recipient's gift e-mail to `email` — the address currently in their row, which may
   * be one the admin just corrected. Same gift, same redemption link; only the e-mail goes out
   * again.
   *
   * Called only after every refusal has passed (campaign lock, resendable status, 24-hour cooldown,
   * address shape), so the caller can send without re-checking. Return a promise and the
   * confirmation toast waits for it — a rejection is left to the caller to report, since only it
   * knows why its request failed. Omit the prop and no resend icon is rendered.
   */
  onResend?: (contact: CampaignContactRow, email: string) => void | Promise<void>;
  /** Open the personalised gift-e-mail preview for this recipient. Omit and no eye icon appears. */
  onPreviewEmail?: (contact: CampaignContactRow) => void;
  /**
   * Remove this recipient from the campaign. Called only when removal is allowed — a redeemed gift
   * and a locked campaign are refused here. Any confirmation step ("their redemption link will stop
   * working") belongs to the caller, which is the side that knows what removal costs. Omit and no
   * X icon appears.
   */
  onRemove?: (contact: CampaignContactRow) => void;
  /**
   * The admin edited this recipient's pending or bounced address, committed on blur — the
   * prototype's `cmpEmailEdit`, which exists so a bounced address can be fixed before resending.
   * Omit the prop and the EMAIL column stays plain text; `canEditEmail` makes a row read-only.
   */
  onEmailChange?: (contact: CampaignContactRow, email: string) => void;
  /**
   * Hover hint for a row's name or email cell. Omit it and no cell carries a title; return
   * undefined for a row that should not have one.
   */
  fieldHint?: (field: "name" | "email", contact: CampaignContactRow) => string | undefined;
  /**
   * Name edited inline, committed on blur with trimmed values (see `ContactNameEditor`). Omit it
   * and the NAME column stays plain text, as it does for rows without `firstName`.
   */
  onNameChange?: (
    contact: CampaignContactRow,
    firstName: string,
    lastName: string,
  ) => void | Promise<void>;
  /** Whether this row's name may be edited inline. Omitted: every row is editable. */
  canEditName?: (contact: CampaignContactRow) => boolean;
  /** The campaign's zone (IANA); defaults to the business zone. Send dates are read in it. */
  timeZone?: string;
  /** The campaign's close instant: an inline send date on or after its day is refused. */
  expireDate?: string | null;
  /** Send date picked inline (YYYY-MM-DD), see `ContactSendDateEditor`. Omit it: plain text. */
  onSendDateChange?: (contact: CampaignContactRow, date: string) => void | Promise<void>;
  /** Whether this row's send date may be edited inline. Omitted: every row is editable. */
  canEditSendDate?: (contact: CampaignContactRow) => boolean;
  /** Rendered in the footer beside Export CSV; nothing when omitted. */
  footerAction?: ReactNode;
  /** Whether this row's address may be edited inline. Omitted: every row is editable. */
  canEditEmail?: (contact: CampaignContactRow) => boolean;
  /**
   * Render the per-row action icons greyed out and inert, keeping the column visible. For a
   * surface whose write endpoints are not implemented yet — the Send Queue's MVP, where every
   * mutation answers 501. The callbacks are still passed and still wired; only the controls are
   * switched off, so re-enabling is the removal of this one prop.
   */
  actionsDisabled?: boolean;
  /** Rendered at the right end of the search/status filter row (the budget strip); nothing when omitted. */
  toolbarEnd?: ReactNode;
  /** Ids of rows added this session: each gets a NEW tag in its Name cell. Row order is the caller's. */
  newContactIds?: ReadonlySet<string>;
  /** Changing this value clears the search and status filters and scrolls the table back to the top. */
  resetSignal?: number;
  /**
   * A non-null reason blocks this row's remove control; the reason is its tooltip and the toast a
   * click answers with. Null or omitted: the control follows the built-in rules.
   */
  removeBlockedReason?: (contact: CampaignContactRow) => string | null;
}

const NAME_TD_CLASS = `${CONTACTS_TD_CLASS} whitespace-nowrap text-rodeo-black`;

const SKELETON_ROWS = [0, 1, 2, 3, 4];

/** How long a confirmation or refusal stays up. */
const TOAST_MS = 4000;

/** Prototype `.bc-modal-btn.bc-modal-btn-primary`. */
const exportButtonSx = {
  px: 2.5,
  py: 1.25,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  borderRadius: "var(--radius-btn)",
  bgcolor: "var(--color-rodeo-black)",
  color: "var(--color-white)",
  "&:hover": { bgcolor: "var(--color-background-dark)" },
  "&.Mui-disabled": {
    bgcolor: "var(--color-rodeo-black)",
    color: "var(--color-white)",
    opacity: 0.45,
  },
} as const;

/** Prototype `.cmp-copy-btn`. */
const copyButtonSx = {
  p: "2px",
  ml: "2px",
  flexShrink: 0,
  color: "var(--color-rodeo-dust)",
  borderRadius: "6px",
  "&:hover": { color: "var(--color-dark-dust)", bgcolor: "var(--color-wax)" },
} as const;

const LOCK_STYLES: Record<CampaignContactsLock, string> = {
  paused: "border-good-ochre-4 bg-good-ochre-bg text-good-ochre",
  closed: "border-smoke bg-wax text-graphite",
};

/** What the snackbar is currently saying, and how loudly. */
interface ToastOutcome {
  message: string;
  severity: "success" | "error" | "warning";
}

export default function CampaignContactsModal({
  open,
  onClose,
  campaignName,
  contacts,
  total,
  loading = false,
  error = null,
  lock = null,
  onResend,
  onPreviewEmail,
  onRemove,
  onEmailChange,
  fieldHint,
  onNameChange,
  canEditName,
  onSendDateChange,
  canEditSendDate,
  timeZone = resolveScheduleTimeZone(null),
  expireDate = null,
  footerAction,
  canEditEmail,
  actionsDisabled = false,
  toolbarEnd,
  newContactIds,
  resetSignal,
  removeBlockedReason,
}: CampaignContactsModalProps) {
  const titleId = useId();
  const statusId = useId();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<string>(ALL_STATUSES);
  const [toastOutcome, setToastOutcome] = useState<ToastOutcome | null>(null);
  /**
   * Held apart from `toastOutcome` so the snackbar keeps its text through the exit transition;
   * clearing the outcome on close would blank the message mid-fade.
   */
  const [toastOpen, setToastOpen] = useState(false);
  /**
   * Addresses the admin has typed but not yet committed, keyed by row id. The caller owns
   * `contacts`, so an edit cannot be written into it here; the draft is what the input shows and
   * what a resend is sent to, and `onEmailChange` hands it to the caller on blur.
   */
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});

  // Each opening starts clean: a search term (or a half-typed address) left over from the previous
  // campaign would silently hide rows of the new one, or e-mail the wrong person.
  useEffect(() => {
    if (open) {
      setSearch("");
      setStatus(ALL_STATUSES);
      setEmailDrafts({});
    }
  }, [open]);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (resetSignal === undefined) return;
    setSearch("");
    setStatus(ALL_STATUSES);
    tableScrollRef.current?.scrollTo?.({ top: 0 });
  }, [resetSignal]);

  const statuses = useMemo(() => contactStatusesPresent(contacts), [contacts]);
  /**
   * The selected status only counts while the data still contains it — the list can change under
   * a mounted modal (a refetch, a different campaign), and a stale selection would otherwise
   * filter every row away with no matching option left in the menu to undo it.
   */
  const activeStatus: CampaignLinkStatus | null = statuses.includes(status as CampaignLinkStatus)
    ? (status as CampaignLinkStatus)
    : null;
  const filtered = useMemo(
    () => filterCampaignContacts(contacts, search, activeStatus),
    [contacts, search, activeStatus],
  );
  const columns = useMemo(() => campaignContactsColumns(contacts), [contacts]);

  // An action nobody can perform is not rendered — no callback, no icon; no callbacks at all, no
  // column.
  const showActions =
    onResend !== undefined || onPreviewEmail !== undefined || onRemove !== undefined;

  const recipientTotal = total ?? contacts.length;
  const isSearching = search.trim().length > 0;
  const subtitle =
    isSearching || activeStatus !== null
      ? COPY.subtitle.filtered(campaignName, filtered.length, recipientTotal, activeStatus)
      : COPY.subtitle.all(campaignName, recipientTotal);

  const columnCount = contactTableColumnCount(columns, showActions);

  /** The NEW tag for a row added this session, shown in its First Name cell. */
  function newTag(contact: CampaignContactRow) {
    return newContactIds?.has(String(contact.id)) ? <NewContactTag /> : null;
  }

  /** The address showing in this row: the uncommitted draft if there is one, else the row's own. */
  function emailFor(contact: CampaignContactRow): string {
    return emailDrafts[String(contact.id)] ?? contact.email;
  }

  function showToast(message: string, severity: ToastOutcome["severity"]) {
    setToastOutcome({ message, severity });
    setToastOpen(true);
  }

  function handleEmailInput(contact: CampaignContactRow, value: string) {
    setEmailDrafts((drafts) => ({ ...drafts, [String(contact.id)]: value }));
  }

  /** Committed on blur, like the prototype's `onchange` — never on every keystroke. */
  function handleEmailCommit(contact: CampaignContactRow) {
    const next = emailFor(contact).trim();
    if (next !== contact.email) onEmailChange?.(contact, next);
  }

  /**
   * The prototype's `cmpResendContact` refusal ladder, in its order: the campaign lock first, then
   * whether this gift can be resent at all, then the 24-hour cooldown, then the address. Each
   * refusal says which one it was — "the campaign is paused" and "you resent an hour ago" need
   * different things from the admin.
   */
  async function handleResend(contact: CampaignContactRow) {
    if (lock === "paused") return showToast(COPY.toast.resendPaused, "warning");
    if (lock === "closed") return showToast(COPY.toast.resendClosed, "warning");
    if (!isResendable(contact)) return showToast(COPY.toast.resendNotResendable, "warning");
    const hoursLeft = resendCooldownHoursLeft(contact);
    if (hoursLeft > 0) return showToast(COPY.toast.resendCoolingDown(hoursLeft), "warning");
    const email = emailFor(contact).trim();
    if (!isValidContactEmail(email)) return showToast(COPY.toast.invalidEmail, "error");
    try {
      await onResend?.(contact, email);
      showToast(COPY.toast.resendSuccess(email), "success");
    } catch {
      // The caller reports its own failure: it made the request and is the only side that knows
      // what went wrong. Claiming "resent" here for a send that never happened would be worse.
    }
  }

  /**
   * The prototype leaves the preview open under a campaign lock; here the whole actions column is
   * inoperable while locked, so this refuses too, with the same paused sentence the other controls
   * use and a closed one shaped like theirs.
   */
  function handlePreview(contact: CampaignContactRow) {
    if (lock === "paused") return showToast(COPY.toast.previewPaused, "warning");
    if (lock === "closed") return showToast(COPY.toast.previewClosed, "warning");
    onPreviewEmail?.(contact);
  }

  /** The prototype's `removeContact` refusals: the lock, redeemed, then any other final status. */
  function handleRemove(contact: CampaignContactRow) {
    if (lock === "paused") return showToast(COPY.toast.removePaused, "warning");
    if (lock === "closed") return showToast(COPY.toast.removeClosed, "warning");
    const blockedReason = removeBlockedReason?.(contact) ?? null;
    if (blockedReason !== null) return showToast(blockedReason, "warning");
    if (contact.status === REDEEMED_STATUS) {
      return showToast(COPY.toast.removeRedeemed(contact.name), "warning");
    }
    if (!isRemovable(contact)) {
      return showToast(COPY.toast.removeNotRemovable(contact.name), "warning");
    }
    onRemove?.(contact);
  }

  function emptyMessage(): string {
    if (contacts.length === 0) return COPY.empty.noContacts;
    if (isSearching || activeStatus === null) return COPY.empty.noMatches;
    return COPY.empty.noneWithStatus(activeStatus);
  }

  function handleExport() {
    // Exports exactly what the table shows, columns included.
    downloadCsv(
      campaignContactsCsvFilename(campaignName, activeStatus),
      buildCampaignContactsCsv(filtered, columns, timeZone),
    );
  }

  /**
   * Copy, and say so either way. `navigator.clipboard` is undefined on an insecure origin and
   * `writeText` rejects when a permissions policy blocks it, so both the missing API and the
   * rejected write end in a message the admin can act on instead of a button that does nothing.
   */
  async function handleCopyUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      showToast(COPY.copyUrlSuccess, "success");
    } catch {
      showToast(COPY.copyUrlFailure, "error");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: CONTACTS_TABLE_PAPER_SX } }}
    >
      <ContactsDialogHeader
        titleId={titleId}
        title={COPY.title}
        subtitle={subtitle}
        onClose={onClose}
      />

      <div className="mt-4 flex flex-wrap items-center gap-2.5">
        <div className="flex max-w-[320px] flex-1 items-center gap-1.5 rounded-lg border border-smoke bg-white px-2.5 py-[7px] focus-within:border-dark-dust">
          <SearchIcon sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
          <input
            autoFocus
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={COPY.searchPlaceholder}
            aria-label={COPY.searchLabel}
            className="w-full min-w-0 bg-transparent text-[13px] text-rodeo-black outline-none placeholder:text-graphite"
          />
        </div>
        <label
          htmlFor={statusId}
          className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.5px] text-graphite"
        >
          {COPY.statusLabel}
          <select
            id={statusId}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="rounded-lg border border-smoke bg-white px-2.5 py-[7px] text-[13px] font-normal normal-case tracking-normal text-rodeo-black focus:border-dark-dust focus:outline-none"
          >
            <option value={ALL_STATUSES}>{COPY.allStatuses}</option>
            {statuses.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>
        {toolbarEnd !== undefined && <div className="ml-auto">{toolbarEnd}</div>}
      </div>

      {lock !== null && (
        <div
          className={`mt-3.5 flex items-center gap-2 rounded-lg border px-3.5 py-2.5 text-[13px] leading-[1.4] ${LOCK_STYLES[lock]}`}
        >
          {lock === "paused" ? (
            <PauseCircleOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          ) : (
            <LockOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
          )}
          <span>{COPY.lock[lock]}</span>
        </div>
      )}

      {loading && (
        <div
          role="status"
          aria-busy="true"
          aria-label={COPY.loadingLabel}
          className="mt-4 flex flex-col gap-2"
        >
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} variant="rounded" height={40} />
          ))}
        </div>
      )}

      {!loading && error !== null && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || COPY.errorFallback}
        </Alert>
      )}

      {!loading && error === null && (
        <div ref={tableScrollRef} className="mt-4 max-h-[56vh] overflow-auto border-t border-smoke">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={CONTACTS_TH_CLASS}>{COPY.columnHeaders.firstName}</th>
                <th className={CONTACTS_TH_CLASS}>{COPY.columnHeaders.lastName}</th>
                <th className={CONTACTS_TH_CLASS}>{COPY.columns.email}</th>
                <th className={CONTACTS_TH_CLASS}>{COPY.columns.status}</th>
                {columns.delivered && (
                  <th className={CONTACTS_TH_CLASS}>{COPY.columns.delivered}</th>
                )}
                {columns.sendDate && <th className={CONTACTS_TH_CLASS}>{COPY.columns.sendDate}</th>}
                {columns.url && <th className={CONTACTS_TH_CLASS}>{COPY.columns.url}</th>}
                {showActions && (
                  <th className={`${CONTACTS_TH_CLASS} text-right`}>
                    {/* The prototype's header cell is blank; the word stays for screen readers. */}
                    <span className="sr-only">{COPY.columns.actions}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={columnCount} className="px-2.5 py-6 text-center text-graphite">
                    {emptyMessage()}
                  </td>
                </tr>
              )}
              {filtered.map((contact) => (
                <tr key={String(contact.id)} className="last:[&>td]:border-b-0">
                  {onNameChange !== undefined &&
                  (canEditName?.(contact) ?? true) &&
                  contact.firstName !== undefined ? (
                    <ContactNameEditor
                      contact={contact}
                      readOnly={lock !== null}
                      title={fieldHint?.("name", contact)}
                      cellClassName={NAME_TD_CLASS}
                      firstCellExtra={newTag(contact)}
                      onCommit={onNameChange}
                      onInvalid={(message) => showToast(message, "error")}
                    />
                  ) : (
                    <ContactNameCells
                      contact={contact}
                      title={fieldHint?.("name", contact)}
                      cellClassName={NAME_TD_CLASS}
                      firstCellExtra={newTag(contact)}
                    />
                  )}
                  <td className={`${CONTACTS_TD_CLASS} whitespace-nowrap text-graphite`}>
                    {onEmailChange === undefined || !(canEditEmail?.(contact) ?? true) ? (
                      <span title={fieldHint?.("email", contact)}>{contact.email}</span>
                    ) : (
                      /* Editable so a pending or bounced address can be corrected — the
                         prototype's `.cmp-email-input`. Read-only, not hidden, while the campaign
                         is locked, so the address stays readable and selectable. */
                      <input
                        type="email"
                        value={emailFor(contact)}
                        readOnly={lock !== null}
                        aria-label={COPY.actions.emailLabel(contact.name)}
                        title={fieldHint?.("email", contact)}
                        onChange={(event) => handleEmailInput(contact, event.target.value)}
                        onBlur={() => handleEmailCommit(contact)}
                        className={INLINE_TEXT_INPUT_CLASS}
                      />
                    )}
                  </td>
                  <td className={CONTACTS_TD_CLASS}>
                    <CampaignStatusChip status={displayStatus(contact)} />
                  </td>
                  {columns.delivered && (
                    <td className={`${CONTACTS_TD_CLASS} whitespace-nowrap text-rodeo-black`}>
                      {/* Read in UTC, and already an em dash for a null date. */}
                      {formatContactDate(contact.deliveredAt)}
                    </td>
                  )}
                  {columns.sendDate && (
                    <td className={`${CONTACTS_TD_CLASS} whitespace-nowrap text-rodeo-black`}>
                      {onSendDateChange !== undefined &&
                      (canEditSendDate?.(contact) ?? true) &&
                      contact.sendDate !== undefined ? (
                        <ContactSendDateEditor
                          contact={contact}
                          readOnly={lock !== null}
                          timeZone={timeZone}
                          expireDate={expireDate}
                          onCommit={onSendDateChange}
                          onInvalid={(message) => showToast(message, "error")}
                        />
                      ) : (
                        formatSendInstant(contact.sendDate, timeZone)
                      )}
                    </td>
                  )}
                  {columns.url && (
                    <UrlCell contact={contact} ended={lock === "closed"} onCopy={handleCopyUrl} />
                  )}
                  {showActions && (
                    <CampaignContactActionsCell
                      contact={contact}
                      lock={lock}
                      disabled={actionsDisabled}
                      onResend={onResend === undefined ? undefined : handleResend}
                      onPreview={onPreviewEmail === undefined ? undefined : handlePreview}
                      onRemove={onRemove === undefined ? undefined : handleRemove}
                      removeBlockedReason={removeBlockedReason?.(contact) ?? null}
                    />
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && error === null && contacts.length < recipientTotal && (
        <p className="mt-3 text-[12px] text-graphite">
          {COPY.showingOf(contacts.length, recipientTotal)}
        </p>
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <Button onClick={onClose} disableRipple sx={CONTACTS_TEXT_LINK_SX}>
          {COPY.close}
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          {footerAction}
          <Button
            variant="contained"
            disableElevation
            onClick={handleExport}
            disabled={filtered.length === 0}
            aria-label={COPY.exportCsvLabel}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
            sx={exportButtonSx}
          >
            {COPY.exportCsv}
          </Button>
        </div>
      </div>

      <Snackbar
        open={toastOpen}
        autoHideDuration={TOAST_MS}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toastOutcome?.severity ?? "success"}
          variant="filled"
          onClose={() => setToastOpen(false)}
          closeText={COPY.copyToastClose}
        >
          {toastOutcome?.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

/**
 * The CAMPAIGN URL cell: the link in a truncated monospace `<code>` whose full value stays
 * reachable — `title` shows it in full on hover, the text itself is real selectable DOM text (CSS
 * truncation only, nothing is cut from the string), and the copy button beside it puts the whole
 * URL on the clipboard. That button is the prototype's `.cmp-copy-btn`.
 */
function UrlCell({
  contact,
  ended,
  onCopy,
}: {
  contact: CampaignContactRow;
  /** An ended campaign's links no longer resolve, so there is nothing worth copying. */
  ended: boolean;
  onCopy: (url: string) => void;
}) {
  const url = contactUrl(contact);
  if (!url) {
    return <td className={`${CONTACTS_TD_CLASS} text-graphite`}>{COPY.emptyValue}</td>;
  }
  return (
    <td className={CONTACTS_TD_CLASS}>
      <span className="inline-flex max-w-[440px] items-center gap-1.5">
        <code
          title={url}
          className="min-w-0 truncate rounded-md bg-clear-dust px-2 py-1 font-mono text-[12px] text-graphite"
        >
          {url}
        </code>
        {!ended && (
          <Tooltip title={COPY.copyUrl}>
            <IconButton
              size="small"
              aria-label={COPY.copyUrlLabel(contact.name)}
              onClick={() => onCopy(url)}
              sx={copyButtonSx}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        )}
      </span>
    </td>
  );
}
